import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, ShieldAlert, RefreshCcw, AlertTriangle, Send } from "lucide-react";
import FinalBrokerPreviewModal from "./broker/FinalBrokerPreviewModal";

function fmt(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

/**
 * PrepareTradeDialog — final validation gate before an order is submitted.
 * Two modes:
 *   1) opportunityId supplied — fetches /portfolios/prepare-trade preview
 *      (used from Opportunities page; does NOT submit — clicking "Analyze"
 *       still routes through /analyze so the user gets the full plan debate).
 *   2) proposalId supplied — this is the approve flow. It refreshes portfolio
 *      state, lets user switch buckets, and calls /proposals/{id}/approve.
 */
export default function PrepareTradeDialog({
  open,
  onClose,
  opportunityId,
  proposalId,
  approveParams,        // { plan_id, entry, stop, target, qty }
  defaultPortfolioKey,
  onApproved,           // (result) => void
}) {
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioKey, setPortfolioKey] = useState(defaultPortfolioKey || "SWING");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [brokerPreviewOpen, setBrokerPreviewOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPortfolioKey(defaultPortfolioKey || "SWING");
  }, [open, defaultPortfolioKey]);

  const loadPortfolios = async () => {
    const { data } = await api.get("/portfolios");
    setPortfolios(data.portfolios || []);
  };

  const loadPreview = async (key) => {
    if (!open) return;
    setLoading(true);
    try {
      if (opportunityId) {
        const { data } = await api.post("/portfolios/prepare-trade", { opportunity_id: opportunityId, portfolio_key: key });
        setPreview(data);
        setPortfolios(data.all_portfolios || []);
      } else if (proposalId && approveParams) {
        // Portfolio-scoped dry run — returns EXACT verdict /approve would compute
        const { data: ports } = await api.get("/portfolios");
        setPortfolios(ports.portfolios || []);
        const { data } = await api.post(`/proposals/${proposalId}/preview-approve`, {
          plan_id: approveParams.plan_id,
          entry: approveParams.entry, stop: approveParams.stop,
          target: approveParams.target, qty: approveParams.qty,
          portfolio_key: key,
        });
        setPreview({ ...data, market: { still_valid: true } });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load trade preview");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      loadPortfolios();
      loadPreview(portfolioKey);
    }
  }, [open, portfolioKey]); // eslint-disable-line

  const submit = async () => {
    if (proposalId && approveParams) {
      setSubmitting(true);
      try {
        const { data } = await api.post(`/proposals/${proposalId}/approve`, {
          ...approveParams, portfolio_key: portfolioKey,
        });
        toast.success(`Order ${data.order.status.toUpperCase()} · Protection ${data.protection_status.toUpperCase()} · Funded from ${data.portfolio_key}`);
        onApproved?.(data);
        onClose?.();
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : d?.message || "Approval failed");
      } finally { setSubmitting(false); }
    }
  };

  const pv = preview?.preview;
  const pf = preview?.portfolio;
  const verdict = preview?.verdict;
  const cashOK = pv ? pv.available_cash_after >= 0 : false;
  const canSubmit = !submitting && cashOK && pv && (proposalId && approveParams) && (!verdict || verdict.pass !== false);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose?.()}>
      <DialogContent className="!rounded-none border-border/70 max-w-3xl" data-testid="prepare-trade-dialog">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-wider text-sm">
            Prepare Trade {pv?.symbol && <>· {pv.symbol}</>}
          </DialogTitle>
        </DialogHeader>

        {loading && !preview && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            <Loader2 className="animate-spin inline mr-2" size={14} /> Refreshing market & validating portfolio…
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            {/* Portfolio selector */}
            <div className="grid grid-cols-3 gap-px bg-border/40">
              {(portfolios || []).map(p => {
                const isSel = portfolioKey === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setPortfolioKey(p.key)}
                    className={`p-3 text-left transition-colors ${isSel ? "bg-secondary/50" : "bg-card/40 hover:bg-secondary/25"}`}
                    data-testid={`prepare-select-portfolio-${p.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-1 h-4 ${isSel ? "bg-[#0A84FF]" : "bg-transparent"}`} />
                      <div className="mono uppercase text-xs">{p.name}</div>
                      {isSel && <CheckCircle2 size={12} className="ml-auto pnl-up" />}
                    </div>
                    <div className="mono text-sm mt-1">${fmt(p.available_cash)}</div>
                    <div className="text-[10px] mono uppercase text-muted-foreground">Avail · {p.allocation_pct}%</div>
                  </button>
                );
              })}
            </div>

            {/* Preview cells */}
            <div className="grid grid-cols-4 gap-px bg-border/40">
              <div className="p-2 bg-card/40"><div className="label-cap">Entry</div><div className="mono text-sm">${fmt(pv.entry)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Stop</div><div className="mono text-sm pnl-down">${fmt(pv.stop)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Target</div><div className="mono text-sm pnl-up">${fmt(pv.target)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Qty</div><div className="mono text-sm">{pv.qty}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Order Value</div><div className="mono text-sm">${fmt(pv.order_value)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Max Loss</div><div className="mono text-sm pnl-down">-${fmt(pv.max_loss)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Potential Profit</div><div className="mono text-sm pnl-up">+${fmt(pv.potential_profit)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">R/R</div><div className="mono text-sm">{fmt(pv.reward_risk)}:1</div></div>
            </div>

            {/* Cash impact */}
            <div className="grid grid-cols-3 gap-px bg-border/40">
              <div className="p-2 bg-card/40"><div className="label-cap">{pf?.name || "Portfolio"} Cash</div><div className="mono text-sm">${fmt(pv.available_cash_before)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Order Impact</div><div className="mono text-sm pnl-down">-${fmt(pv.order_value)}</div></div>
              <div className={`p-2 bg-card/40 ${cashOK ? "" : "border border-signal-red/50 bg-signal-red/5"}`}>
                <div className="label-cap">Remaining Cash</div>
                <div className={`mono text-sm ${cashOK ? "pnl-up" : "pnl-down"}`}>${fmt(pv.available_cash_after)}</div>
              </div>
            </div>

            {/* Market freshness (only from opportunity preview) */}
            {preview.market && preview.market.still_valid === false && (
              <div className="grid-cell !p-3 border-signal-yellow/50 bg-signal-yellow/5 flex items-center gap-2 text-xs">
                <AlertTriangle size={14} className="text-signal-yellow" />
                Market has moved &gt;5% since this opportunity was scanned — rerun analysis before submitting.
              </div>
            )}

            {/* Risk verdict */}
            {verdict && (
              <div className="border border-border/60">
                <div className="flex items-center gap-2 px-3 h-9 border-b border-border/40">
                  <span className="label-cap">Portfolio-Scoped Risk Engine</span>
                  <span className={`chip ${verdict.pass ? "pnl-up border-current" : "pnl-down border-current"} ml-auto`}>
                    {verdict.pass ? <><ShieldCheck size={12}/> PASS</> : <><ShieldAlert size={12}/> BLOCK</>}
                  </span>
                </div>
                <div className="p-3 space-y-1 max-h-32 overflow-auto">
                  {verdict.checks?.map((k, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      {k.pass ? <CheckCircle2 size={12} className="pnl-up shrink-0 mt-0.5"/> : <ShieldAlert size={12} className="pnl-down shrink-0 mt-0.5"/>}
                      <span className="text-foreground">{k.name}</span>
                      <span className="text-muted-foreground">— {k.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!proposalId && opportunityId && (
              <div className="grid-cell !p-3 border-border/60 text-xs text-muted-foreground flex items-center gap-2">
                <RefreshCcw size={12} />
                Preview only — run full AI analysis from the Opportunities page to select a plan and approve. This dialog reflects portfolio validation for the suggested trade.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button className="btn-outline" onClick={onClose} disabled={submitting}>Cancel</button>
          {proposalId && approveParams && (
            <>
              <button
                className="btn-outline"
                onClick={() => setBrokerPreviewOpen(true)}
                disabled={submitting || !canSubmit}
                data-testid="prepare-broker-preview-btn"
                title="Stage B — Alpaca Paper submission via new order-intent flow"
              >
                <Send size={12}/> Submit Paper Order (Stage B)
              </button>
              <button
                className="btn-primary"
                onClick={submit}
                disabled={!canSubmit}
                data-testid="prepare-submit-btn"
              >
                {submitting ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                Approve & Submit (Legacy)
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      {proposalId && approveParams && (
        <FinalBrokerPreviewModal
          open={brokerPreviewOpen}
          onClose={() => setBrokerPreviewOpen(false)}
          proposalId={proposalId}
          planId={approveParams.plan_id}
          portfolioKey={portfolioKey}
          orderType="market"
          onSubmitted={(intent) => { onApproved?.({ intent }); onClose?.(); }}
        />
      )}
    </Dialog>
  );
}
